import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import {
  Box, Flex, Text, Button, Link,
  Table, Thead, Tbody, Tr, Th, Td, VStack, Heading,
  Badge, Spinner, useToast
} from "@chakra-ui/react";
import HeaderMenus from './HeaderMenus';

function ClaimCodingDashboard() {
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, has_more: true });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const bgColor = 'white';
  const borderColor = 'gray.200';
  const tableHeaderBg = 'gray.50';

  const loadResults = async (loadMore = false) => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    const currentOffset = loadMore ? pagination.offset + pagination.limit : 0;

    try {
      const response = await callApi(`/api/v1/claim-coding-results?limit=${pagination.limit}&offset=${currentOffset}`, accessToken);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load coding results');
      }

      const data = await response.json();

      if (data.status === 'success') {
        setResults(prev => loadMore ? [...prev, ...data.results] : data.results);
        setPagination(data.pagination);
      } else {
        throw new Error('Failed to load coding results');
      }
    } catch (err) {
      const errorMessage = `Error loading data: ${err.message}`;
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadResults();
    }
  }, [accessToken]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getBadgeColorScheme = (type) => {
    switch (type.toLowerCase()) {
      case 'pro': return 'blue';
      case 'tech': return 'green';
      case 'cpt': return 'purple';
      case 'icd10': return 'orange';
      default: return 'gray';
    }
  };

  const getScoreBadgeScheme = (score) => {
    if (score === null || score === undefined) return 'gray';
    if (score >= 90) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  return (
    <Box>
      <HeaderMenus />
      <Box bg={bgColor} p={6} minH="calc(100vh - 60px)">
        {error && (
          <Box p={4} mb={4} borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50">
            <Text color="red.600">{error}</Text>
          </Box>
        )}

        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Heading size="md">Claim Coding Results</Heading>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => loadResults()}
            isLoading={isLoading && results.length === 0}
          >
            Refresh Data
          </Button>
        </Flex>

        {isLoading && results.length === 0 ? (
          <Flex justify="center" align="center" height="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <>
            <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>Case ID</Th>
                    <Th>Case Description</Th>
                    <Th>Claim Type</Th>
                    <Th>Code Type</Th>
                    <Th>Codes</Th>
                    <Th>Confidence Score</Th>
                    <Th>Date Coded</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.map((result, index) => (
                    <Tr key={index}>
                      <Td fontWeight="medium">
                        <Link as={RouterLink} to={`/full-cpt-coder?caseId=${result.internal_case_id}`} color="blue.600" isExternal>
                          {result.external_case_id}
                        </Link>
                      </Td>
                      <Td>{result.case_description}</Td>
                      <Td>
                        <Badge colorScheme={getBadgeColorScheme(result.claim_type)}>
                          {result.claim_type.toUpperCase()}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={getBadgeColorScheme(result.code_type)}>
                          {result.code_type.toUpperCase()}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="xs">{result.codes_summary.join(', ')}</Text>
                      </Td>
                      <Td>
                        {result.coder_score !== null ? (
                          <Badge colorScheme={getScoreBadgeScheme(result.coder_score)}>
                            {result.coder_score}
                          </Badge>
                        ) : (
                          <Text fontSize="xs" color="gray.500">N/A</Text>
                        )}
                      </Td>
                      <Td>{formatDate(result.date_created)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {pagination.has_more && (
              <Flex justify="center" mt={4}>
                <Button
                  onClick={() => loadResults(true)}
                  isLoading={isLoading}
                  variant="outline"
                >
                  Load More
                </Button>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default ClaimCodingDashboard;